import {
  VALVE_BADGE_SYMBOLS,
  VALVE_CLOSE_FAILURE_COLOR,
  VALVE_INPUT_PENDING_COLOR,
  VALVE_OPEN_FAILURE_COLOR,
  getMarkerFillColor,
  getMarkerStrokeColor,
  getValveBadge,
  getValveStateBadge,
  getValveStateFlags,
} from '../markerColors'

describe('getMarkerFillColor', () => {
  it('shows offline modules in red, even when a valve is open', () => {
    expect(getMarkerFillColor({ online: false, hasOpenValve: true })).toBe(
      '#FF0000'
    )
  })

  it('shows online modules with an open valve in blue', () => {
    expect(getMarkerFillColor({ online: true, hasOpenValve: true })).toBe(
      '#1647F3'
    )
  })

  it('shows online modules waiting for pressure in green', () => {
    expect(
      getMarkerFillColor({
        online: true,
        hasOpenValve: true,
        hasValveInputPending: true,
      })
    ).toBe(VALVE_INPUT_PENDING_COLOR)
  })

  it('keeps modules waiting for pressure red while offline', () => {
    expect(
      getMarkerFillColor({ online: false, hasValveInputPending: true })
    ).toBe('#FF0000')
  })

  it('shows online modules without an open valve in white', () => {
    expect(getMarkerFillColor({ online: true, hasOpenValve: false })).toBe(
      'white'
    )
  })

  it('keeps the existing marker color when online status is absent', () => {
    expect(getMarkerFillColor({ hasOpenValve: true })).toBe('#1647F3')
  })
})

describe('getMarkerStrokeColor', () => {
  it('keeps the unread-error outline visible on offline modules', () => {
    expect(getMarkerStrokeColor({ online: false, hasUnreadError: true })).toBe(
      '#FFB000'
    )
  })

  it('uses the existing red outline for unread errors on online modules', () => {
    expect(getMarkerStrokeColor({ online: true, hasUnreadError: true })).toBe(
      '#FF0000'
    )
  })
})

describe('getMarkerStrokeColor with valve failures', () => {
  it('uses the red outline for a valve that did not open', () => {
    expect(
      getMarkerStrokeColor({ online: true, hasValveOpenFailure: true })
    ).toBe('#FF0000')
  })

  it('uses the red outline for a valve that did not close', () => {
    expect(
      getMarkerStrokeColor({ online: true, hasValveCloseFailure: true })
    ).toBe('#FF0000')
  })

  it('keeps the white outline without errors', () => {
    expect(getMarkerStrokeColor({ online: true })).toBe('white')
  })

  it('keeps the white outline while a valve is waiting for pressure', () => {
    expect(
      getMarkerStrokeColor({ online: true, hasValveInputPending: true })
    ).toBe('white')
  })
})

describe('getValveBadge', () => {
  it('shows an orange failure badge for a valve that did not open', () => {
    expect(getValveBadge({ hasValveOpenFailure: true })).toEqual({
      color: VALVE_OPEN_FAILURE_COLOR,
      symbol: VALVE_BADGE_SYMBOLS.FAILURE,
    })
  })

  it('shows a yellow failure badge for a valve that did not close', () => {
    expect(getValveBadge({ hasValveCloseFailure: true })).toEqual({
      color: VALVE_CLOSE_FAILURE_COLOR,
      symbol: VALVE_BADGE_SYMBOLS.FAILURE,
    })
  })

  it('shows a green pending badge for a valve waiting for pressure', () => {
    expect(getValveBadge({ online: true, hasValveInputPending: true })).toEqual(
      {
        color: VALVE_INPUT_PENDING_COLOR,
        symbol: VALVE_BADGE_SYMBOLS.PENDING,
      }
    )
  })

  it('uses a pending color that differs from the failure colors', () => {
    expect(VALVE_INPUT_PENDING_COLOR).not.toBe(VALVE_OPEN_FAILURE_COLOR)
    expect(VALVE_INPUT_PENDING_COLOR).not.toBe(VALVE_CLOSE_FAILURE_COLOR)
  })

  it('does not show the pending badge for offline modules', () => {
    expect(
      getValveBadge({ online: false, hasValveInputPending: true })
    ).toBeNull()
  })

  it('shows only the more serious open failure when both occur', () => {
    expect(
      getValveBadge({
        hasValveOpenFailure: true,
        hasValveCloseFailure: true,
        hasValveInputPending: true,
      }).color
    ).toBe(VALVE_OPEN_FAILURE_COLOR)
  })

  it('prefers failures over the pending badge', () => {
    expect(
      getValveBadge({ hasValveCloseFailure: true, hasValveInputPending: true })
        .symbol
    ).toBe(VALVE_BADGE_SYMBOLS.FAILURE)
  })

  it('returns null without failures', () => {
    expect(getValveBadge({})).toBeNull()
  })
})

describe('getValveStateBadge', () => {
  it('maps the valve state to its badge', () => {
    expect(getValveStateBadge({ inputMismatch: 'open_failed' }).color).toBe(
      VALVE_OPEN_FAILURE_COLOR
    )
    expect(getValveStateBadge({ inputMismatch: 'close_failed' }).color).toBe(
      VALVE_CLOSE_FAILURE_COLOR
    )
    expect(
      getValveStateBadge({ inputMismatch: 'none', inputPending: true }).symbol
    ).toBe(VALVE_BADGE_SYMBOLS.PENDING)
    expect(getValveStateBadge({ inputMismatch: 'none' })).toBeNull()
    expect(getValveStateBadge({})).toBeNull()
  })
})

describe('getValveStateFlags', () => {
  it('returns no flags without valves, so the flags of the module are kept', () => {
    expect(getValveStateFlags([])).toEqual({})
    expect(getValveStateFlags()).toEqual({})
  })

  it('derives the flags from the valves of the module', () => {
    expect(
      getValveStateFlags([
        { inputMismatch: 'none', inputPending: true, currentValue: 1 },
        {
          inputMismatch: 'close_failed',
          inputPending: false,
          currentValue: 0,
          currentIsSuspended: true,
        },
      ])
    ).toEqual({
      hasOpenValve: true,
      hasSuspendedValve: true,
      hasValveOpenFailure: false,
      hasValveCloseFailure: true,
      hasValveInputPending: true,
    })
  })

  it('treats the float value of the valve list as open', () => {
    expect(getValveStateFlags([{ currentValue: 1.0 }]).hasOpenValve).toBe(true)
  })

  it('resets the flags when the valves are fine again', () => {
    expect(
      getValveStateFlags([
        { inputMismatch: 'none', currentValue: 0, currentIsSuspended: false },
      ])
    ).toEqual({
      hasOpenValve: false,
      hasSuspendedValve: false,
      hasValveOpenFailure: false,
      hasValveCloseFailure: false,
      hasValveInputPending: false,
    })
  })
})
